import {
  getProductReleaseShipmentPath,
  getProductReleaseSummaryPath,
  isProductReleaseShipmentView,
} from '@/utility/productReleasePaths'
import { isProductReleaseV1Enabled } from '@/utility/productReleaseStatsigGateConstants'

export const FILE_UPLOAD_QUEUE_SEGMENT = 'file-upload-queue'

export const FILE_UPLOAD_QUEUE_DOCUMENT_TAB = 'passive-logger-data'

export const FILE_UPLOAD_QUEUE_LIMIT_MESSAGE = 'You can upload up to 50 files and 100 MB at a time'

export const FILE_UPLOAD_QUEUE_EMPTY_MESSAGE =
  'No files waiting for review. Upload files to add temperature data to this shipment.'

export const FILE_UPLOAD_QUEUE_CAPTION_TYPES =
  'PDF, DOC, DOCX, CSV, XLS, XLSX, TTV, ZIP, JPG, JPEG and PNG. Up to 50 files and 100 MB per upload.'

export const FILE_UPLOAD_QUEUE_CAPTION_PARSING =
  'Parsing runs in the background. Files over 10 MB, and DOC, DOCX, CSV, TTV and image files, are kept but not parsed.'

/**
 * Where Add Temp Data / Add data should go.
 * Gate off keeps today's upload page. Gate on splits Upload Files and Add Device Manually.
 * @param {boolean} gateEnabled - File Upload Queue gate.
 * @param {'current' | 'upload-files' | 'add-manually'} choice - Menu choice, or the direct click when the gate is off.
 * @returns {'current-upload' | 'queue'} Destination for the click.
 */
export function resolveAddTemperatureDataDestination(gateEnabled, choice) {
  if (!gateEnabled || choice === 'add-manually' || choice === 'current') {
    return 'current-upload'
  }
  return 'queue'
}

/**
 * @param {string | undefined} shipmentUuid - Shipment the queue belongs to.
 * @returns {string} Command Center route for the queue.
 */
export function commandCenterQueuePath(shipmentUuid) {
  return `/shipment/devices/${shipmentUuid}/${FILE_UPLOAD_QUEUE_SEGMENT}`
}

/**
 * @param {string | undefined} view - Route view segment.
 * @param {string | undefined} shipmentUuid - Shipment the queue belongs to.
 * @returns {string} File Upload Queue list route for the current shipment view.
 */
export function fileUploadQueuePath(view, shipmentUuid) {
  const segment = view || 'devices'
  return `/shipment/${segment}/${shipmentUuid}/${FILE_UPLOAD_QUEUE_SEGMENT}`
}

/**
 * @param {string | undefined} view - Route view segment.
 * @param {string | undefined} shipmentUuid - Shipment the queue belongs to.
 * @param {string} documentId - Queue document id.
 * @param {string | null | undefined} deviceId - Optional device serial when opening a nested row.
 * @returns {string} File Details route.
 */
export function fileUploadQueueDetailsPath(view, shipmentUuid, documentId, deviceId) {
  const base = `${fileUploadQueuePath(view, shipmentUuid)}/${documentId}`
  if (!deviceId) return base
  const query = new URLSearchParams({ deviceId })
  return `${base}?${query.toString()}`
}

/**
 * Where Go Back and the shipment breadcrumb return.
 * Product Release v1 review returns to that review. Every other Product Release open returns to the summary.
 * @param {object} params
 * @param {string | undefined} params.view - Route view segment.
 * @param {string | undefined} params.shipmentUuid - Shipment the queue belongs to.
 * @param {object | null | undefined} params.statsigClient - Statsig client.
 * @param {boolean} params.returnToTemperatureReview - Set when the queue was opened from temperature review.
 * @returns {string} Path for Go Back.
 */
export function fileUploadQueueReturnPath({
  view,
  shipmentUuid,
  statsigClient,
  returnToTemperatureReview,
}) {
  if (!isProductReleaseShipmentView(view)) {
    return `/shipment/devices/${shipmentUuid}`
  }
  if (returnToTemperatureReview && isProductReleaseV1Enabled(statsigClient)) {
    return getProductReleaseSummaryPath(statsigClient, shipmentUuid, 'temperature_review')
  }
  return getProductReleaseSummaryPath(statsigClient, shipmentUuid)
}

/**
 * Today's upload page for the same shipment. Used when the File Upload Queue gate is off.
 * @param {string | undefined} view - Route view segment.
 * @param {string | undefined} shipmentUuid - Shipment the queue belongs to.
 * @param {object | null | undefined} statsigClient - Statsig client.
 * @returns {string} Existing upload-v2 path.
 */
export function legacyTemperatureUploadPath(view, shipmentUuid, statsigClient) {
  if (isProductReleaseShipmentView(view)) {
    return getProductReleaseShipmentPath(statsigClient, shipmentUuid, 'upload-v2')
  }
  return `/shipment/devices/${shipmentUuid}/upload-v2`
}
